import { useState, type FormEvent } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { UserPlusIcon } from 'lucide-react';
import { PERMISSIONS } from '@siteflow/shared';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { CopyField } from '@/components/copy-field';
import { PageHeader } from '@/components/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { messageForError } from '@/lib/api-client';
import { invitationsQuery, memberKeys, membersApi, membersQuery } from '@/lib/api/members';
import type { Member } from '@/lib/api/types';
import { getAuthState } from '@/lib/auth-store';
import { BASE_PATH } from '@/lib/base-path';
import { HELPER_ROLES, rebaseInviteUrl, roleLabel } from '@/lib/client-kit';
import { useOrgPermissions } from '@/lib/permissions';
import { formatDate } from '@/lib/utils';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function PeoplePage() {
  const { orgId } = Route.useParams();
  const queryClient = useQueryClient();
  const { has } = useOrgPermissions(orgId);
  const canInvite = has(PERMISSIONS.ORG_MEMBERS_INVITE);
  const canRemove = has(PERMISSIONS.ORG_MEMBERS_REMOVE);
  const currentUserId = getAuthState().user?.id;

  const members = useQuery(membersQuery(orgId));
  const invitations = useQuery({ ...invitationsQuery(orgId), enabled: canInvite });

  const [email, setEmail] = useState('');
  const [roleKey, setRoleKey] = useState<string>(HELPER_ROLES[0].key);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [link, setLink] = useState<{ email: string; url: string } | null>(null);
  const [removing, setRemoving] = useState<Member | null>(null);

  const refresh = () => void queryClient.invalidateQueries({ queryKey: memberKeys.all(orgId) });

  const invite = useMutation({
    mutationFn: () => membersApi.invite(orgId, { email: email.trim().toLowerCase(), roleKey }),
    onSuccess: (created) => {
      // The address people actually use to sign in, not the one the API knows about.
      const here = `${window.location.origin}${BASE_PATH}`;
      setLink({ email: created.email, url: rebaseInviteUrl(created.inviteUrl, here) });
      setEmail('');
      refresh();
    },
    onError: (error) => toast.error(messageForError(error, 'We could not create that invitation.')),
  });

  const revoke = useMutation({
    mutationFn: (invitationId: string) => membersApi.revokeInvitation(orgId, invitationId),
    onSuccess: () => {
      toast.success('Invitation cancelled');
      refresh();
    },
    onError: (error) => toast.error(messageForError(error, 'We could not cancel that invitation.')),
  });

  const remove = useMutation({
    mutationFn: (memberId: string) => membersApi.remove(orgId, memberId),
    onSuccess: () => {
      toast.success('Removed');
      setRemoving(null);
      refresh();
    },
    onError: (error) => toast.error(messageForError(error, 'We could not remove them.')),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!EMAIL.test(email.trim())) {
      setEmailError('Enter a valid email address.');
      return;
    }
    setEmailError(null);
    setLink(null);
    invite.mutate();
  };

  const pending = (invitations.data ?? []).filter((item) => !item.acceptedAt && !item.revokedAt);

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      <PageHeader
        title="People"
        description="Who can change this website. Add a colleague and they get their own sign-in."
      />

      {canInvite ? (
        <Card>
          <CardHeader>
            <CardTitle>Add someone</CardTitle>
            <CardDescription>
              You get a link to send them. It works once, and they choose their own password.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form noValidate onSubmit={submit} className="flex flex-col gap-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="helper-email">Their email</Label>
                  <Input
                    id="helper-email"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    disabled={invite.isPending}
                    placeholder="name@example.org"
                  />
                  {emailError ? (
                    <p role="alert" className="text-micro text-danger">
                      {emailError}
                    </p>
                  ) : null}
                </div>

                <fieldset className="flex flex-col gap-1.5">
                  <legend className="text-data font-medium text-ink">What they can do</legend>
                  {HELPER_ROLES.map((role) => (
                    <label key={role.key} className="flex cursor-pointer items-start gap-2 text-data text-ink">
                      <input
                        type="radio"
                        name="helper-role"
                        value={role.key}
                        checked={roleKey === role.key}
                        onChange={() => setRoleKey(role.key)}
                        className="mt-1"
                      />
                      <span>
                        {role.label}
                        <span className="block text-micro text-ink-subtle">{role.hint}</span>
                      </span>
                    </label>
                  ))}
                </fieldset>
              </div>

              <Button type="submit" disabled={invite.isPending} className="self-start">
                <UserPlusIcon /> {invite.isPending ? 'Creating link…' : 'Create sign-in link'}
              </Button>
            </form>

            {link ? (
              <div className="mt-4 flex flex-col gap-2 rounded-lg border border-hairline bg-sunken p-4">
                <p className="text-data text-ink">
                  Send this link to <strong>{link.email}</strong>. It is not shown again.
                </p>
                <CopyField label="Sign-in link" value={link.url} />
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Who has access</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col divide-y divide-hairline">
          {members.isPending ? (
            <Skeleton className="h-10 w-full" />
          ) : members.isError ? (
            <p role="alert" className="text-data text-danger">
              {messageForError(members.error, 'The list could not be loaded.')}
            </p>
          ) : (
            (members.data ?? [])
              .filter((member) => member.status === 'active')
              .map((member) => {
                const isSelf = member.userId === currentUserId;
                return (
                  <div key={member.id} className="flex items-center gap-3 py-2.5">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-data font-medium text-ink">
                        {member.name}
                        {isSelf ? <span className="font-normal text-ink-subtle"> (you)</span> : null}
                      </p>
                      <p className="truncate text-micro text-ink-subtle">{member.email}</p>
                    </div>
                    <span className="text-data text-ink-muted">
                      {roleLabel(member.roleKeys[0], member.isOwner)}
                    </span>
                    {canRemove && !isSelf && !member.isOwner ? (
                      <Button variant="ghost" size="sm" onClick={() => setRemoving(member)}>
                        Remove
                      </Button>
                    ) : null}
                  </div>
                );
              })
          )}
        </CardContent>
      </Card>

      {canInvite && pending.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Waiting to join</CardTitle>
            <CardDescription>They have a link but have not chosen a password yet.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col divide-y divide-hairline">
            {pending.map((item) => (
              <div key={item.id} className="flex items-center gap-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-data font-medium text-ink">{item.email}</p>
                  <p className="text-micro text-ink-subtle">
                    {roleLabel(item.roleKey)} · link works until {formatDate(item.expiresAt)}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={revoke.isPending}
                  onClick={() => revoke.mutate(item.id)}
                >
                  Cancel
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      ) : null}

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => {
          if (!open) setRemoving(null);
        }}
        title={`Remove ${removing?.name ?? 'this person'}?`}
        confirmLabel="Remove"
        destructive
        pending={remove.isPending}
        description="They will be signed out and can no longer change the website. What they already published stays."
        onConfirm={() => removing && remove.mutate(removing.id)}
      />
    </div>
  );
}

export const Route = createFileRoute('/app/$orgId/sites/$siteId/people')({
  component: PeoplePage,
});
